import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '~/components/ui/typography';
import { YouTubeInlinePlayer } from '~/components/ui/youtube-inline-player';
import { cn } from '~/lib/utils';

interface Testimonial {
  embedUrl: string;
  previewImgUrl: string;
  title: string;
  description: string;
}

interface TestimonialSwitcherProps {
  testimonials: Testimonial[];
}

export function TestimonialSwitcher({ testimonials }: TestimonialSwitcherProps) {
  const [selectedTestimonial, setSelectedTestimonial] = useState(0);
  let timer: NodeJS.Timeout | undefined = undefined;
  useEffect(() => {
    timer = setInterval(() => {
      setSelectedTestimonial((prevTestimonial) => (prevTestimonial + 1) % testimonials.length);
    }, 5000);

    return () => {
      clearInterval(timer);
    };
  }, []);
  const handleTestimonialClick = useCallback(
    (index: number) => {
      setSelectedTestimonial(index);
      clearInterval(timer);
    },
    [timer]
  );

  const handleOnPlay = useCallback(() => {
    clearInterval(timer);
  }, [timer]);

  return (
    <div className="flex flex-col items-center justify-center lg:flex-row">
      <div className="aspect-[16/10] w-full lg:w-[40%]">
        <YouTubeInlinePlayer
          embedUrl={testimonials[selectedTestimonial].embedUrl}
          previewImgUrl={testimonials[selectedTestimonial].previewImgUrl}
          onPlay={handleOnPlay}
        >
          <div className="px-2 text-center">
            <Typography variant="h4" as="p" className="font-fraunces text-lg font-bold xl:text-xl">
              {testimonials[selectedTestimonial].title}
            </Typography>
            <Typography className="text-sm font-light xl:text-base">
              {testimonials[selectedTestimonial].description}
            </Typography>
          </div>
        </YouTubeInlinePlayer>
      </div>
      <div className="flex w-full items-center lg:aspect-[16/10] lg:w-[50%] xl:w-[40%]">
        <div className="w-full">
          {testimonials.map((testimonial, index) => (
            <div
              onClick={handleTestimonialClick.bind(null, index)}
              className={cn(
                'cursor-pointer rounded-r-lg px-6 py-4 text-primary-foreground transition xl:px-9 xl:py-6',
                { 'border-b hover:bg-primary/50 hover:text-primary-foreground': index !== selectedTestimonial },
                { 'bg-primary': index === selectedTestimonial }
              )}
              key={testimonial.embedUrl + index}
            >
              <Typography as="p" className="font-fraunces text-lg font-bold xl:text-xl">
                {testimonial.title}
              </Typography>
              <Typography variant="mutedText" className="text-sm xl:text-base">
                {testimonial.description}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
